import { Box, Container, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ButtonNav from "../components/General/ButtonNav";
import { useAuth } from "../context/AuthContext";
import { NavIcon } from "../config/navigation/navigation.icons";

import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import DataObjectIcon from "@mui/icons-material/DataObject";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";

function SectionHeader({ icon, title, isDark }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 3,
          display: "grid",
          placeItems: "center",
          background: isDark ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.86)",
          border: "1px solid rgba(25,118,210,0.22)",
          boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
          color: "primary.main",
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 900, lineHeight: 1.15 }}>{title}</Typography>
    </Box>
  );
}

function GradientDivider({ isDark }) {
  return (
    <Box
      sx={{
        height: 2,
        borderRadius: 999,
        my: 3,
        background: isDark
          ? "linear-gradient(90deg, transparent, rgba(25,118,210,0.5), transparent)"
          : "linear-gradient(90deg, transparent, rgba(25,118,210,0.35), transparent)",
      }}
    />
  );
}

export default function Settings() {
  const { hasPermission, auth } = useAuth();
  const isSuperAdmin = auth.user?.id === 1;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Agrupado por el módulo al que afecta cada configuración (mismo orden que el
  // menú de navegación), para que se entienda a qué parte del sistema impacta.
  const groups = [
    {
      key: "general",
      title: "General",
      icon: <SettingsRoundedIcon />,
      items: [
        hasPermission("administrar configuracion") && {
          key: "general-settings",
          title: "Configuración general",
          subtitle: "Kill-switch de consultas en línea al Poder Judicial",
          icon: <TuneRoundedIcon />,
          color: "#dc2626",
          to: "/settings/general",
        },
        hasPermission("ver sucursales") && {
          key: "branches",
          title: "Sucursales",
          subtitle: "Gestión de sucursales activas",
          icon: <StoreRoundedIcon />,
          color: "#0ea5e9",
          to: "/branches",
        },
        hasPermission("administrar estados personalizados") && {
          key: "lawsuit-tax-status",
          title: "Estados del Sistema",
          subtitle: "Catálogo general de estados, reutilizable por varios módulos",
          icon: <TuneRoundedIcon />,
          color: "#059669",
          to: "/settings/lawsuit-tax-status",
        },
      ].filter(Boolean),
    },
    {
      key: "legalFile",
      title: "Expedientes",
      icon: <NavIcon iconKey="assignment" />,
      items: [
        hasPermission("ver tipos operaciones") && {
          key: "operation-types",
          title: "Tipos de Operaciones",
          subtitle: "Catálogo de tipos de operación de expedientes, clasificados por origen",
          icon: <GavelRoundedIcon />,
          color: "#b45309",
          to: "/settings-operation-types",
        },
        hasPermission("ver etapas expediente") && {
          key: "legal-file-stages",
          title: "Etapas de Expediente",
          subtitle: "Configurar las etapas y qué tipos de operación impulsan cada una",
          icon: <TimelineRoundedIcon />,
          color: "#0f766e",
          to: "/settings-legal-file-stages",
        },
        hasPermission("ver martilleros") && {
          key: "auctioneers",
          title: "Martilleros",
          subtitle: "Registro de martilleros: nombre y matrícula que van en los escritos de designación",
          icon: <RecordVoiceOverRoundedIcon />,
          color: "#be185d",
          to: "/settings-auctioneers",
        },
        hasPermission("administrar tipos adjuntos") && {
          key: "attachment-types",
          title: "Tipo de Escritos",
          subtitle: "Habilitar, deshabilitar y configurar la plantilla de cada tipo de escrito",
          icon: <FolderSpecialRoundedIcon />,
          color: "#7c3aed",
          to: "/settings-attachment-types",
        },
        isSuperAdmin && hasPermission("administrar tipos adjuntos") && {
          key: "attachment-sources",
          title: "Fuentes de Documentos",
          subtitle: "Configuración técnica interna (solo admin)",
          icon: <DataObjectIcon />,
          color: "#334155",
          to: "/settings-attachment-sources",
        },
      ].filter(Boolean),
    },
    {
      key: "liquidations",
      title: "Liquidaciones",
      icon: <NavIcon iconKey="liquidations" />,
      items: [
        hasPermission("administrar catalogos liquidacion") && {
          key: "liquidation-settings",
          title: "Liquidaciones Municipalidad",
          subtitle: "Tipos de rubros, juzgados, Caja/Colegio, valores JUS y tasas de justicia",
          icon: <CalculateRoundedIcon />,
          color: "#1565c0",
          to: "/settings-liquidation",
        },
        hasPermission("administrar catalogos liquidacion comun") && {
          key: "individual-liquidation-settings",
          title: "Liquidaciones Comunes",
          subtitle: "Tipos de rubros, juzgados, Caja/Colegio y tasas de interés (Pasiva / TIM)",
          icon: <CalculateRoundedIcon />,
          color: "#2563eb",
          to: "/settings-individual-liquidation",
        },
        hasPermission("ver marcadores sentencia") && {
          key: "sentence-markers",
          title: "Marcadores de Sentencia Municipalidad",
          subtitle: "Operaciones que marcan sentencia y rubros que suman en cada columna de la Planilla Municipalidad",
          icon: <BalanceRoundedIcon />,
          color: "#9d174d",
          to: "/settings-sentence-markers",
        },
      ].filter(Boolean),
    },
  ].filter((group) => group.items.length > 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {groups.map((group, idx) => (
        <Box key={group.key} sx={{ mt: idx === 0 ? 2 : 0 }}>
          {idx > 0 && <GradientDivider isDark={isDark} />}
          <SectionHeader icon={group.icon} title={group.title} isDark={isDark} />
          <Grid container spacing={2}>
            {group.items.map((item) => (
              <Grid key={item.key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <ButtonNav
                  title={item.title}
                  subtitle={item.subtitle}
                  icon={item.icon}
                  color={item.color}
                  to={item.to}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Container>
  );
}
